import { useState, useMemo } from "react";
import { Link, useRoute } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type NetworkIntro,
  type NetworkMessage,
} from "@/lib/store";

/**
 * SisterNetworkIntros
 *
 * Inbox + outbox for structured intros.
 *
 * Tabs:
 *  1. Incoming  - pending intros sent to me. Three actions per row:
 *                 Accept (open ongoing thread), Accept once (single-exchange
 *                 thread that closes after the recipient's first reply), or
 *                 Decline silently (sender is never notified).
 *  2. Sent      - pending intros I sent, read-only. We never reveal the
 *                 receiver's response status until they accept.
 *  3. Threads   - accepted and one-time threads that have not lapsed.
 *                 Tapping a row opens the message thread inline.
 *
 * Auto-lapse after 14 days of silence is enforced by the query, not here.
 * One-time threads close on the receiver's first reply; the mutation
 * handles that transition.
 */

type TabKey = "incoming" | "sent" | "threads";

function timeAgo(ts: number): string {
  const minutes = Math.max(1, Math.floor((Date.now() - ts) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / (60 * 24))}d ago`;
}

const EMPTY_INTROS = { incomingPending: [], sentPending: [], accepted: [] };

export default function SisterNetworkIntros() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: accept/decline/reply below WRITE with this
  // id. See the early return just below.
  const userId = me?.id ?? null;
  const intros = useStoreQuery(() => (userId ? queries.myActiveIntros(userId) : EMPTY_INTROS));

  // Allow direct deep-link to a thread via /sister-network/intros/:id
  const [, params] = useRoute<{ id: string }>("/sister-network/intros/:id");
  const initialThreadId = params?.id ?? null;

  const [tab, setTab] = useState<TabKey>(
    initialThreadId ? "threads" : (intros.incomingPending.length > 0 ? "incoming" : "threads"),
  );
  const [openThreadId, setOpenThreadId] = useState<string | null>(initialThreadId);

  const counts = {
    incoming: intros.incomingPending.length,
    sent: intros.sentPending.length,
    threads: intros.accepted.length,
  };

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see your intros, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="Sister Network"
        title="Intros and threads"
        subtitle={
          <>
            "Iron sharpens iron, so one sister sharpens another."
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · Proverbs 27:17</span>
          </>
        }
        trailing={
          <Link href="/sister-network" data-testid="link-back-to-network">
            <button
              className="hover-elevate"
              style={{
                width: 36, height: 36, borderRadius: 999,
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "var(--mnf-cream)",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              aria-label="Back to network"
            >
              <Icon name="arrow_back" size={18} color="var(--mnf-cream)" />
            </button>
          </Link>
        }
      />

      <div className="px-5 pt-4 pb-28 space-y-4" style={{ background: "var(--mnf-cream)" }}>
        {/* Tab bar */}
        <div
          className="flex gap-1 mb-4"
          style={{
            background: "rgba(30,74,53,0.06)",
            borderRadius: 14,
            padding: 4,
          }}
          role="tablist"
        >
          {(["incoming", "sent", "threads"] as TabKey[]).map((k) => {
            const active = tab === k;
            const label =
              k === "incoming" ? "Incoming"
              : k === "sent" ? "Sent"
              : "Threads";
            return (
              <button
                key={k}
                role="tab"
                aria-selected={active}
                onClick={() => { setTab(k); setOpenThreadId(null); }}
                data-testid={`tab-${k}`}
                className="flex-1 hover-elevate"
                style={{
                  border: "none",
                  borderRadius: 11,
                  padding: "9px 8px",
                  background: active ? "var(--mnf-cream)" : "transparent",
                  color: active ? "var(--mnf-fd)" : "var(--mnf-tg)",
                  fontSize: 12.5,
                  fontWeight: active ? 600 : 500,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  fontFamily: "var(--mnf-body)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                {label}
                {counts[k] > 0 && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center", justifyContent: "center",
                      minWidth: 18, height: 18,
                      padding: "0 6px",
                      borderRadius: 999,
                      background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.18)",
                      color: active ? "var(--mnf-cream)" : "var(--mnf-fd)",
                      fontSize: 10, fontWeight: 600,
                    }}
                  >
                    {counts[k]}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === "incoming" && (
          <IncomingList intros={intros.incomingPending} userId={userId} />
        )}
        {tab === "sent" && (
          <SentList intros={intros.sentPending} userId={userId} />
        )}
        {tab === "threads" && (
          openThreadId ? (
            <ThreadView
              introId={openThreadId}
              userId={userId}
              onBack={() => setOpenThreadId(null)}
            />
          ) : (
            <ThreadsList
              intros={intros.accepted}
              userId={userId}
              onOpen={(id) => setOpenThreadId(id)}
            />
          )
        )}
      </div>
    </>
  );
}

/* ============================== Incoming ================================== */

function IncomingList({ intros, userId }: { intros: NetworkIntro[]; userId: string }) {
  if (intros.length === 0) {
    return (
      <EmptyState
        icon="mail"
        title="A quiet inbox"
        body="When another sister reaches out, her intro will land here. Take your time before you respond."
      />
    );
  }
  return (
    <div className="space-y-3" data-testid="list-incoming">
      {intros.map((i) => (
        <IncomingCard key={i.id} intro={i} userId={userId} />
      ))}
    </div>
  );
}

function IncomingCard({ intro, userId }: { intro: NetworkIntro; userId: string }) {
  const sender = useStoreQuery(() => queries.userById(intro.fromUserId));
  const senderProfile = useStoreQuery(() => queries.myNetworkProfile(intro.fromUserId));
  const acceptIntro = useStoreMutation(mutations.acceptIntro);
  const declineIntro = useStoreMutation(mutations.declineIntro);
  const acceptIntroOneTime = useStoreMutation(mutations.acceptIntroOneTime);
  const [acted, setActed] = useState<"accepted" | "once" | "declined" | null>(null);

  void userId; // viewerId is implicit in toUserId === userId from query

  const onAccept = () => { acceptIntro(intro.id); setActed("accepted"); };
  const onAcceptOnce = () => { acceptIntroOneTime(intro.id); setActed("once"); };
  const onDecline = () => { declineIntro(intro.id); setActed("declined"); };

  if (acted) {
    const message =
      acted === "accepted" ? "Thread opened. You can reply when you're ready."
      : acted === "once" ? "One exchange opened. The thread closes after her first reply."
      : "Declined silently. She will not be notified.";
    return (
      <GlassCard padding={16} data-testid={`card-intro-acted-${intro.id}`}>
        <div className="flex items-center gap-3">
          <div style={{
            width: 36, height: 36, borderRadius: 999,
            background: "rgba(127,119,221,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--mnf-purple)",
          }}>
            <Icon name="check" size={18} />
          </div>
          <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
            {message}
          </p>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={16} data-testid={`card-incoming-${intro.id}`}>
      <div className="flex items-start gap-3">
        <Avatar
          initials={sender?.initials ?? "S"}
          src={sender?.avatarDataUrl ?? undefined}
          size={44}
          ringColor="var(--mnf-purpleL)"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p className="mnf-serif" style={{ fontSize: 15.5, fontWeight: 500, color: "var(--mnf-fd)" }}>
              {sender?.name ?? "A sister"}
            </p>
            <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              {timeAgo(intro.createdAt)}
            </p>
          </div>
          {senderProfile?.lifeStage && (
            <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 1 }}>
              {senderProfile.lifeStage.replace("-", " ")}
            </p>
          )}
        </div>
      </div>

      <p
        className="mnf-body italic mt-3"
        style={{
          fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.55,
          padding: "10px 12px",
          background: "rgba(255,255,255,0.6)",
          borderLeft: "3px solid var(--mnf-purpleL)",
          borderRadius: 8,
        }}
        data-testid={`text-intro-prompt-${intro.id}`}
      >
        "{intro.prompt}"
      </p>

      {intro.customNote && (
        <p
          className="mnf-body mt-2"
          style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.55, padding: "0 4px" }}
          data-testid={`text-intro-note-${intro.id}`}
        >
          {intro.customNote}
        </p>
      )}

      {senderProfile && senderProfile.goals.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {senderProfile.goals.map((g) => (
            <PillTag key={g} tone="purple">{g.replace("-", " ")}</PillTag>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mt-4">
        <Btn
          variant="primary"
          size="sm"
          onClick={onAccept}
          data-testid={`button-accept-${intro.id}`}
        >
          Accept
        </Btn>
        <Btn
          variant="secondary"
          size="sm"
          onClick={onAcceptOnce}
          data-testid={`button-accept-once-${intro.id}`}
        >
          Accept once
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          onClick={onDecline}
          data-testid={`button-decline-${intro.id}`}
        >
          Decline
        </Btn>
      </div>

      <p className="mnf-body mt-3" style={{ fontSize: 11, color: "var(--mnf-tg)", lineHeight: 1.5, textAlign: "center" }}>
        Decline is silent. She will not be notified either way.
      </p>
    </GlassCard>
  );
}

/* ================================ Sent ==================================== */

function SentList({ intros, userId }: { intros: NetworkIntro[]; userId: string }) {
  void userId;
  if (intros.length === 0) {
    return (
      <EmptyState
        icon="send"
        title="Nothing pending"
        body="When you send a structured intro, you'll see it here while you wait. We don't track read receipts."
      />
    );
  }
  return (
    <div className="space-y-3" data-testid="list-sent">
      {intros.map((i) => (
        <SentCard key={i.id} intro={i} />
      ))}
    </div>
  );
}

function SentCard({ intro }: { intro: NetworkIntro }) {
  const recipient = useStoreQuery(() => queries.userById(intro.toUserId));
  return (
    <GlassCard padding={16} data-testid={`card-sent-${intro.id}`}>
      <div className="flex items-start gap-3">
        <Avatar
          initials={recipient?.initials ?? "S"}
          src={recipient?.avatarDataUrl ?? undefined}
          size={40}
          ringColor="rgba(30,74,53,0.20)"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p className="mnf-serif" style={{ fontSize: 14.5, fontWeight: 500, color: "var(--mnf-fd)" }}>
              To {recipient?.name ?? "a sister"}
            </p>
            <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              {timeAgo(intro.createdAt)}
            </p>
          </div>
          <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 2 }}>
            Awaiting response
          </p>
        </div>
      </div>

      <p
        className="mnf-body italic mt-3"
        style={{
          fontSize: 12.5, color: "var(--mnf-fd)", lineHeight: 1.5,
          padding: "8px 10px",
          background: "rgba(255,255,255,0.55)",
          borderLeft: "3px solid rgba(30,74,53,0.18)",
          borderRadius: 8,
        }}
      >
        "{intro.prompt}"
      </p>

      {intro.customNote && (
        <p
          className="mnf-body mt-2"
          style={{ fontSize: 12.5, color: "var(--mnf-fd)", lineHeight: 1.5, padding: "0 4px" }}
        >
          {intro.customNote}
        </p>
      )}
    </GlassCard>
  );
}

/* ============================== Threads =================================== */

function ThreadsList({
  intros, userId, onOpen,
}: {
  intros: NetworkIntro[];
  userId: string;
  onOpen: (id: string) => void;
}) {
  if (intros.length === 0) {
    return (
      <EmptyState
        icon="forum"
        title="No active threads"
        body="Once a sister accepts your intro, or you accept hers, the conversation will live here."
      />
    );
  }
  return (
    <div className="space-y-3" data-testid="list-threads">
      {intros.map((i) => (
        <ThreadRow key={i.id} intro={i} userId={userId} onOpen={onOpen} />
      ))}
    </div>
  );
}

function ThreadRow({
  intro, userId, onOpen,
}: {
  intro: NetworkIntro;
  userId: string;
  onOpen: (id: string) => void;
}) {
  const otherId = intro.fromUserId === userId ? intro.toUserId : intro.fromUserId;
  const other = useStoreQuery(() => queries.userById(otherId));
  const messages = useStoreQuery(() => queries.networkMessagesForIntro(intro.id));
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastBody = lastMessage?.body ?? intro.prompt;
  const lastTs = lastMessage?.sentAt ?? intro.lastMessageAt ?? intro.createdAt;

  return (
    <button
      onClick={() => onOpen(intro.id)}
      data-testid={`row-thread-${intro.id}`}
      className="w-full text-left hover-elevate active-elevate-2"
      style={{
        background: "rgba(255,255,255,0.7)",
        border: "1px solid rgba(30,74,53,0.08)",
        borderRadius: 16,
        padding: 14,
        cursor: "pointer",
        display: "block",
      }}
    >
      <div className="flex items-start gap-3">
        <Avatar
          initials={other?.initials ?? "S"}
          src={other?.avatarDataUrl ?? undefined}
          size={42}
          ringColor={intro.status === "one-time" ? "var(--mnf-purpleL)" : "var(--mnf-fL)"}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
              {other?.name ?? "A sister"}
            </p>
            <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              {timeAgo(lastTs)}
            </p>
          </div>
          <p
            className="mnf-body"
            style={{
              fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.45,
              marginTop: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {lastBody}
          </p>
          {intro.status === "one-time" && (
            <div className="mt-2">
              <PillTag tone="purple">one-time</PillTag>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

/* ============================ Thread view ================================= */

function ThreadView({
  introId, userId, onBack,
}: {
  introId: string;
  userId: string;
  onBack: () => void;
}) {
  const intros = useStoreQuery(() => queries.myActiveIntros(userId));
  const intro = useMemo<NetworkIntro | null>(() => {
    return (
      intros.accepted.find((i) => i.id === introId) ?? null
    );
  }, [intros, introId]);

  const otherId = intro
    ? (intro.fromUserId === userId ? intro.toUserId : intro.fromUserId)
    : "";
  const other = useStoreQuery(() => queries.userById(otherId));
  const messages = useStoreQuery(() => queries.networkMessagesForIntro(introId));
  const sendMessage = useStoreMutation(mutations.sendNetworkMessage);
  const [draft, setDraft] = useState("");

  if (!intro) {
    return (
      <GlassCard padding={18}>
        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.55 }}>
          This thread is no longer active. It may have lapsed or completed.
        </p>
        <Btn variant="secondary" block size="md" onClick={onBack} className="mt-3" data-testid="button-thread-back">
          Back to threads
        </Btn>
      </GlassCard>
    );
  }

  // For one-time threads, the receiver replies once and the thread closes.
  // The receiver is the user who is NOT the original fromUserId.
  const isReceiver = intro.fromUserId !== userId;
  const receiverHasReplied = messages.some((m) => m.fromUserId !== intro.fromUserId);
  const oneTimeClosed = intro.status === "one-time" && receiverHasReplied;
  // For one-time, only the receiver sends the closing reply. The original sender
  // cannot keep messaging once the thread is one-time.
  const canSend =
    !oneTimeClosed &&
    (intro.status === "accepted" || (intro.status === "one-time" && isReceiver && !receiverHasReplied));

  const onSend = () => {
    const body = draft.trim();
    if (!body) return;
    sendMessage({ introId, fromUserId: userId, body });
    setDraft("");
  };

  return (
    <div data-testid={`thread-view-${introId}`}>
      {/* Thread header */}
      <div
        className="flex items-center gap-3 mb-3"
        style={{
          padding: "10px 12px",
          background: "rgba(255,255,255,0.6)",
          border: "1px solid rgba(30,74,53,0.08)",
          borderRadius: 14,
        }}
      >
        <button
          onClick={onBack}
          data-testid="button-thread-back"
          aria-label="Back to threads"
          className="hover-elevate"
          style={{
            width: 32, height: 32, borderRadius: 999,
            background: "rgba(30,74,53,0.06)",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--mnf-f)",
          }}
        >
          <Icon name="arrow_back" size={16} />
        </button>
        <Avatar
          initials={other?.initials ?? "S"}
          src={other?.avatarDataUrl ?? undefined}
          size={36}
          ringColor={intro.status === "one-time" ? "var(--mnf-purpleL)" : "var(--mnf-fL)"}
        />
        <div className="flex-1 min-w-0">
          <p className="mnf-serif" style={{ fontSize: 14.5, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}>
            {other?.name ?? "A sister"}
          </p>
          <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 2 }}>
            {intro.status === "one-time" ? "One-time exchange" : "Open thread"}
          </p>
        </div>
      </div>

      {/* Original prompt as the seed of the thread */}
      <div
        className="mb-3"
        style={{
          padding: "10px 12px",
          background: "rgba(127,119,221,0.08)",
          border: "1px solid rgba(127,119,221,0.18)",
          borderRadius: 12,
        }}
      >
        <p className="mnf-eyebrow mb-1" style={{ color: "var(--mnf-tg)" }}>Seed prompt</p>
        <p className="mnf-body italic" style={{ fontSize: 12.5, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
          "{intro.prompt}"
        </p>
        {intro.customNote && (
          <p className="mnf-body mt-1.5" style={{ fontSize: 12.5, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
            {intro.customNote}
          </p>
        )}
      </div>

      {/* Messages */}
      <div className="space-y-2 mb-3" data-testid="thread-messages">
        {messages.length === 0 ? (
          <p
            className="mnf-body text-center"
            style={{ fontSize: 12, color: "var(--mnf-tg)", padding: "16px 8px", lineHeight: 1.5 }}
          >
            No messages yet. Begin gently when you're ready.
          </p>
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} message={m} mine={m.fromUserId === userId} />
          ))
        )}
      </div>

      {/* Composer */}
      {canSend ? (
        <div
          style={{
            padding: 12,
            background: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(30,74,53,0.10)",
            borderRadius: 14,
          }}
        >
          <textarea
            data-testid="input-thread-message"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 600))}
            rows={3}
            placeholder={
              intro.status === "one-time"
                ? "Your one reply, sister. Make it honest."
                : "Reply with care..."
            }
            className="w-full mnf-body outline-none resize-none"
            style={{
              background: "transparent",
              border: "none",
              fontSize: 13,
              color: "var(--mnf-fd)",
              lineHeight: 1.5,
              padding: 0,
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>
              {draft.length} / 600
            </p>
            <Btn
              variant="primary"
              size="sm"
              onClick={onSend}
              disabled={draft.trim().length === 0}
              data-testid="button-send-message"
            >
              Send <Icon name="send" size={12} />
            </Btn>
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: "12px 14px",
            background: "rgba(127,119,221,0.08)",
            border: "1px solid rgba(127,119,221,0.18)",
            borderRadius: 12,
          }}
        >
          <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
            {oneTimeClosed
              ? "This was a one-time exchange and is now closed. If you both want to keep walking, send a fresh intro."
              : "This thread is closed."}
          </p>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message, mine }: { message: NetworkMessage; mine: boolean }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`message-${message.id}`}
        style={{
          maxWidth: "82%",
          padding: "10px 12px",
          borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          background: mine ? "var(--mnf-f)" : "rgba(255,255,255,0.78)",
          color: mine ? "var(--mnf-cream)" : "var(--mnf-fd)",
          border: mine ? "none" : "1px solid rgba(30,74,53,0.08)",
        }}
      >
        <p className="mnf-body" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {message.body}
        </p>
        <p
          className="mnf-body"
          style={{
            fontSize: 10,
            marginTop: 4,
            color: mine ? "rgba(245,240,232,0.7)" : "var(--mnf-tg)",
            textAlign: mine ? "right" : "left",
          }}
        >
          {timeAgo(message.sentAt)}
        </p>
      </div>
    </div>
  );
}

/* ============================ Empty state ================================= */

function EmptyState({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <GlassCard padding={22} data-testid="empty-state">
      <div className="text-center">
        <div
          style={{
            width: 56, height: 56, borderRadius: 999,
            background: "rgba(30,74,53,0.08)",
            color: "var(--mnf-f)",
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <Icon name={icon} size={24} />
        </div>
        <h3 className="mnf-serif" style={{ fontSize: 19, fontWeight: 500, color: "var(--mnf-fd)" }}>
          {title}
        </h3>
        <p className="mnf-body mt-2" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
          {body}
        </p>
      </div>
    </GlassCard>
  );
}
